import { z } from 'zod';
import {
    CardPointeTerminalSaleLifecyclePayloadType,
    type CardPointeTerminalSaleLifecyclePayload,
    type CardPointeTerminalSaleLegacyRequestLifecyclePayload,
    type CardPointeTerminalSaleRequestLifecyclePayloadV1,
    type CardPointeTerminalSaleRequestLifecyclePayloadV2,
    type CardPointeTerminalSaleResponseLifecyclePayload,
    type StoredCardPointeTerminalSaleRequestLifecyclePayloadV1,
} from '../../../types/ipg/cardpointe/cardpointe-terminal-sale-lifecycle.types';
import {
    CardPointeTerminalSaleLegacyRequestDetailsSchema,
    CardPointeTerminalSaleRequestDetailsSchema,
} from './cardpointe-terminal-sale.schemas';
import { CardPointeTerminalSaleResponseDetailsSchema } from './cardpointe-terminal-sale-response.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerFieldsAtPath,
} from '../../../stored-json-number';

const CardPointeHttpStatusSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'cardpointe-terminal-sale-lifecycle.http-status',
    provenance: {
        kind: 'bounded-owned-schema',
        schemaName: 'CardPointeTerminalSaleResponseDetailsSchema.httpStatus',
        minimum: 100,
        maximum: 599,
    },
});

export const CardPointeTerminalSaleRequestLifecyclePayloadV1Schema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeTerminalSaleLifecyclePayloadType.REQUEST_V1
    >;
    request_details: typeof CardPointeTerminalSaleRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        CardPointeTerminalSaleLifecyclePayloadType.REQUEST_V1
    ),
    request_details: CardPointeTerminalSaleRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeTerminalSaleRequestLifecyclePayloadV1>;

export const CardPointeTerminalSaleLegacyRequestLifecyclePayloadSchema: z.ZodType<CardPointeTerminalSaleLegacyRequestLifecyclePayload> =
    z.object({
        gateway_lifecycle_payload_type: z.literal(
            CardPointeTerminalSaleLifecyclePayloadType.REQUEST_V1
        ),
        request_details: CardPointeTerminalSaleLegacyRequestDetailsSchema,
    });

export const StoredCardPointeTerminalSaleRequestLifecyclePayloadV1Schema: z.ZodType<StoredCardPointeTerminalSaleRequestLifecyclePayloadV1> =
    z.union([
        CardPointeTerminalSaleRequestLifecyclePayloadV1Schema,
        CardPointeTerminalSaleLegacyRequestLifecyclePayloadSchema,
    ]);

export const CardPointeTerminalSaleRequestLifecyclePayloadV2Schema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeTerminalSaleLifecyclePayloadType.REQUEST_V2
    >;
    request_details: typeof CardPointeTerminalSaleRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(
        CardPointeTerminalSaleLifecyclePayloadType.REQUEST_V2
    ),
    request_details: CardPointeTerminalSaleRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeTerminalSaleRequestLifecyclePayloadV2>;

export const CardPointeTerminalSaleResponseLifecyclePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeTerminalSaleLifecyclePayloadType.RESPONSE
    >;
    response_details: typeof CardPointeTerminalSaleResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CardPointeTerminalSaleLifecyclePayloadType.RESPONSE),
    response_details: CardPointeTerminalSaleResponseDetailsSchema,
}) satisfies z.ZodType<CardPointeTerminalSaleResponseLifecyclePayload>;

export const StoredCardPointeTerminalSaleResponseLifecyclePayloadSchema: z.ZodType<CardPointeTerminalSaleResponseLifecyclePayload> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerFieldsAtPath(value, {
                safeIntegerDomain: CardPointeHttpStatusSafeIntegerDomain,
                path: ['response_details'],
                fields: ['httpStatus'],
            }),
        CardPointeTerminalSaleResponseLifecyclePayloadSchema
    );

export const CardPointeTerminalSaleLifecyclePayloadSchema: z.ZodType<CardPointeTerminalSaleLifecyclePayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CardPointeTerminalSaleRequestLifecyclePayloadV2Schema,
        CardPointeTerminalSaleResponseLifecyclePayloadSchema,
    ]);
